import {
  FiPieChart,
  FiUsers,
  FiTag,
  FiShield,
  FiUser,
  FiDollarSign,
  FiFileText,
  FiTrendingUp,
  FiCreditCard,
  FiMessageSquare,
  FiArchive,
  FiBookOpen,
  FiRefreshCw,
  FiUploadCloud,
  FiGift,
} from "react-icons/fi";

import { LandingPage } from "../pages/LandingPage";
import { HomePage } from "../pages/HomePage";
import { UsersPage } from "../pages/UsersPage";
import { RolesPage } from "../pages/RolesPage";
import { PermissionsPage } from "../pages/PermissionsPage";
import { ProfilePage } from "../pages/ProfilePage";
import { TransactionsPage } from "../pages/TransactionsPage";
import { RequestsPage } from "../pages/RequestsPage";
import { BalancePage } from "../pages/BalancePage";
import { LoginPage } from "../pages/LoginPage";
import { FundsPage } from "../pages/FundsPage";
import { FundDetailPage } from "../pages/FundDetailPage";
import { InvestmentPage } from "../pages/InvestmentPage";
import { AboutUsPage } from "../pages/AboutUsPage";
import { ServicesPage } from "../pages/ServicesPage";
import { ServiceDetailPage } from "../pages/ServiceDetailPage";
import { DedicatedPortfolioPage } from "../pages/DedicatedPortfolioPage";
import { ContactPage } from "../pages/ContactPage";
import { BlogPage } from "../pages/BlogPage";
import { NewsPage } from "../pages/NewsPage";
import { NewsCategoryPage } from "../pages/NewsCategoryPage";
import { NewsDetailPage } from "../pages/NewsDetailPage";
import { NewsManagementPage } from "../pages/NewsManagementPage";
import { MessagesPage } from "../pages/MessagesPage";
import { FundsCategoryPage } from "../pages/FundsCategoryPage";
import { CurlRunnerPage } from "../pages/CurlRunnerPage";
import { PaymentAppReturnPage } from "../pages/PaymentAppReturnPage";
import { PaymentResultPage } from "../pages/PaymentResultPage";
import { ExternalRequestsPage } from "../pages/ExternalRequestsPage";
import { CertificatesPage } from "../pages/CertificatesPage";
import { InvestCardexPage } from "../pages/InvestCardexPage";
import { TurnoverAccountPage } from "../pages/TurnoverAccountPage";
import { PaymentBatchCardsPage } from "../pages/PaymentBatchCardsPage";
import { PaymentCardsPage } from "../pages/PaymentCardsPage";
import { PaymentSlipsPage } from "../pages/PaymentSlipsPage";
import { BanksPage } from "../pages/BanksPage";
import { ClubPage } from "../pages/ClubPage";
import { ClubRewardsPage } from "../pages/ClubRewardsPage";
import { CustomerClubPage } from "../pages/CustomerClubPage";

import { RouteConfig, SidebarSection } from "../types/routes";

// Route path constants for type safety
export const ROUTE_PATHS = {
  HOME: "/",
  LANDING: "/",
  FUNDS: "/funds",
  FUND_CATEGORY: "/funds/category/:category",
  FUND_DETAIL: "/funds/:fundId",
  FUND_INVEST: "/funds/:fundId/invest",
  ABOUT: "/about",
  SERVICES: "/services",
  SERVICE_DETAIL: "/services/:serviceId",
  DEDICATED_PORTFOLIO: "/dedicated-portfolio",
  CUSTOMER_CLUB: "/customer-club",
  CONTACT: "/contact",
  NEWS: "/news",
  NEWS_CATEGORY: "/news/:category",
  NEWS_DETAIL: "/news/:category/:slug/:seoTitle",
  BLOG: "/blog",
  LOGIN: "/omidex",
  CURL: "/curl",
  PAYMENT_APP_RETURN: "/payment-result/app/:requestId",
  PAYMENT_RESULT: "/payment-result/:requestId",
  DASHBOARD: {
    ROOT: "/dashboard",
    HOME: "/dashboard/home",
    USERS: "/dashboard/users",
    ROLES: "/dashboard/roles",
    PERMISSIONS: "/dashboard/permissions",
    PROFILE: "/dashboard/profile",
    TRANSACTIONS: "/dashboard/transactions",
    REQUESTS: "/dashboard/requests",
    PAYMENT_BATCH_CARDS: "/dashboard/payment-batch-cards",
    PAYMENT_CARDS: "/dashboard/payment-cards",
    PAYMENT_SLIPS: "/dashboard/payment-slips",
    BANKS: "/dashboard/banks",
    BALANCE: "/dashboard/balance",
    EXTERNAL_REQUESTS: "/dashboard/external-requests",
    CERTIFICATES: "/dashboard/certificates",
    INVEST_CARDEX: "/dashboard/cardex",
    TURNOVER_ACCOUNT: "/dashboard/turnover",
    CLUB: "/dashboard/club",
    CLUB_REWARDS: "/dashboard/club-rewards",
    NEWS: "/dashboard/news",
    MESSAGES: "/dashboard/messages",
  },
} as const;

export const getServiceDetailPath = (serviceId: string) =>
  `/services/${serviceId}`;
export const getNewsCategoryPath = (category: string) => `/news/${category}`;
export const getNewsDetailPath = (
  category: string,
  slug: string,
  seoTitle: string,
) => `/news/${category}/${slug}/${seoTitle}`;

// Sidebar sections configuration
export const SIDEBAR_SECTIONS: Record<string, SidebarSection> = {
  DASHBOARD: { title: "پنل سرمایه گذار", order: 1 },
  USER_SECTION: { title: "بخش کاربری", order: 2 },
  MAIN: { title: "مدیریت سیستم", order: 3 },
};

// All application routes
export const routes: RouteConfig[] = [
  // Public routes
  {
    path: ROUTE_PATHS.LANDING,
    component: LandingPage,
    title: "صفحه اصلی",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.FUNDS,
    component: FundsPage,
    title: "صندوق‌ها",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.FUND_CATEGORY,
    component: FundsCategoryPage,
    title: "دسته‌بندی صندوق‌ها",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.FUND_DETAIL,
    component: FundDetailPage,
    title: "جزئیات صندوق",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.FUND_INVEST,
    component: InvestmentPage,
    title: "سرمایه‌گذاری",
    requiresAuth: true,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.ABOUT,
    component: AboutUsPage,
    title: "درباره ما",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.SERVICES,
    component: ServicesPage,
    title: "خدمات",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.SERVICE_DETAIL,
    component: ServiceDetailPage,
    title: "جزئیات خدمات",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.DEDICATED_PORTFOLIO,
    component: DedicatedPortfolioPage,
    title: "سبدگردانی اختصاصی",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.CUSTOMER_CLUB,
    component: CustomerClubPage,
    title: "باشگاه مشتریان",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.CONTACT,
    component: ContactPage,
    title: "تماس با ما",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.NEWS,
    component: NewsPage,
    title: "اطلاع‌رسانی",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.NEWS_DETAIL,
    component: NewsDetailPage,
    title: "جزئیات خبر",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.NEWS_CATEGORY,
    component: NewsCategoryPage,
    title: "دسته‌بندی اطلاع‌رسانی",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.BLOG,
    component: BlogPage,
    title: "وبلاگ",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.LOGIN,
    component: LoginPage,
    title: "ورود",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  // {
  //   path: ROUTE_PATHS.CURL,
  //   component: CurlRunnerPage,
  //   title: "Curl Runner",
  //   requiresAuth: false,
  //   layout: "public",
  //   showInSidebar: false,
  // },
  {
    path: ROUTE_PATHS.PAYMENT_APP_RETURN,
    component: PaymentAppReturnPage,
    title: "بازگشت به اپلیکیشن",
    requiresAuth: false,
    layout: "public",
    showInSidebar: false,
  },
  {
    path: ROUTE_PATHS.PAYMENT_RESULT,
    component: PaymentResultPage,
    title: "نتیجه پرداخت",
    requiresAuth: true,
    layout: "public",
    showInSidebar: false,
  },

  // Dashboard routes
  {
    path: ROUTE_PATHS.DASHBOARD.HOME,
    component: HomePage,
    title: "صفحه اصلی",
    icon: FiPieChart,
    requiresAuth: true,
    permissions: ["home:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "DASHBOARD",
  },

  // User Section Routes
  {
    path: ROUTE_PATHS.DASHBOARD.REQUESTS,
    component: RequestsPage,
    title: "درخواست‌ها",
    icon: FiFileText,
    requiresAuth: true,
    permissions: ["requests:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.TRANSACTIONS,
    component: TransactionsPage,
    title: "تراکنش‌ها",
    icon: FiDollarSign,
    requiresAuth: true,
    permissions: ["transactions:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.CLUB,
    component: ClubPage,
    title: "باشگاه مشتریان",
    icon: FiGift,
    requiresAuth: true,
    permissions: ["club:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.TURNOVER_ACCOUNT,
    component: TurnoverAccountPage,
    title: "گردش حساب",
    icon: FiRefreshCw,
    requiresAuth: true,
    permissions: ["turnoverAccount:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.CERTIFICATES,
    component: CertificatesPage,
    title: "گواهی‌ سرمایه‌گذاری",
    icon: FiBookOpen,
    requiresAuth: true,
    permissions: ["certificates:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.PROFILE,
    component: ProfilePage,
    title: "پروفایل",
    icon: FiUser,
    requiresAuth: true,
    permissions: ["profile:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.PAYMENT_BATCH_CARDS,
    component: PaymentBatchCardsPage,
    title: "بسته‌های کارت هدیه",
    icon: FiArchive,
    requiresAuth: true,
    permissions: ["paymentBatchCards:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.PAYMENT_CARDS,
    component: PaymentCardsPage,
    title: "کارت‌های پرداخت",
    icon: FiCreditCard,
    requiresAuth: true,
    permissions: ["paymentCards:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.PAYMENT_SLIPS,
    component: PaymentSlipsPage,
    title: "فیش‌های پرداخت",
    icon: FiUploadCloud,
    requiresAuth: true,
    permissions: ["paymentSlips:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.BALANCE,
    component: BalancePage,
    title: "موجودی",
    icon: FiTrendingUp,
    requiresAuth: true,
    permissions: ["balance:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.EXTERNAL_REQUESTS,
    component: ExternalRequestsPage,
    title: "کلیه درخواست‌ها",
    icon: FiArchive,
    requiresAuth: true,
    permissions: ["externalRequests:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.INVEST_CARDEX,
    component: InvestCardexPage,
    title: "کاردکس سرمایه‌گذار",
    icon: FiFileText,
    requiresAuth: true,
    permissions: ["investCardex:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.NEWS,
    component: NewsManagementPage,
    title: "اخبار",
    icon: FiFileText,
    requiresAuth: true,
    permissions: ["news:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.MESSAGES,
    component: MessagesPage,
    title: "پیام‌ها",
    icon: FiMessageSquare,
    requiresAuth: true,
    permissions: ["messages:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.BANKS,
    component: BanksPage,
    title: "بانک‌ها",
    icon: FiArchive,
    requiresAuth: true,
    permissions: ["banks:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "USER_SECTION",
  },

  // Admin/Management Routes
  {
    path: ROUTE_PATHS.DASHBOARD.CLUB_REWARDS,
    component: ClubRewardsPage,
    title: "جوایز باشگاه",
    icon: FiGift,
    requiresAuth: true,
    permissions: ["clubRewards:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "MAIN",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.USERS,
    component: UsersPage,
    title: "کاربران",
    icon: FiUsers,
    requiresAuth: true,
    permissions: ["users:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "MAIN",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.ROLES,
    component: RolesPage,
    title: "نقش‌ها",
    icon: FiTag,
    requiresAuth: true,
    permissions: ["roles:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "MAIN",
  },
  {
    path: ROUTE_PATHS.DASHBOARD.PERMISSIONS,
    component: PermissionsPage,
    title: "مجوزها",
    icon: FiShield,
    requiresAuth: true,
    permissions: ["permissions:view"],
    layout: "dashboard",
    showInSidebar: true,
    sidebarSection: "MAIN",
  },
];

// Utility: Filter routes by user permissions (OR logic)
export const filterRoutesByPermissions = (
  routeConfigs: RouteConfig[],
  userPermissions: string[],
  isSuperAdmin: boolean = false,
  userRoleValue?: string,
): RouteConfig[] => {
  return routeConfigs.filter((route) => {
    // Super admin has access to everything
    if (isSuperAdmin) {
      return true;
    }

    // Legacy superAdmin support: if role value is "superAdmin" and no permissions, grant access
    if (
      userRoleValue === "superAdmin" &&
      (!userPermissions || userPermissions.length === 0)
    ) {
      return true;
    }

    // If no permissions required, route is accessible
    if (!route.permissions || route.permissions.length === 0) {
      return true;
    }

    // User needs at least one of the required permissions (OR logic)
    return route.permissions.some((perm) => userPermissions.includes(perm));
  });
};

// Utility: Get sidebar navigation items grouped by section
export const getSidebarItems = (
  routeConfigs: RouteConfig[],
  userPermissions: string[],
  isSuperAdmin: boolean = false,
  userRoleValue?: string,
): Record<string, RouteConfig[]> => {
  const filtered = filterRoutesByPermissions(
    routeConfigs,
    userPermissions,
    isSuperAdmin,
    userRoleValue,
  );
  const sidebarRoutes = filtered.filter((r) => r.showInSidebar);

  // Group by section
  const grouped: Record<string, RouteConfig[]> = {};
  sidebarRoutes.forEach((route) => {
    const section = route.sidebarSection || "OTHER";
    if (!grouped[section]) grouped[section] = [];
    grouped[section].push(route);
  });

  return grouped;
};

// Utility: Get route config by path
export const getRouteByPath = (
  path: string,
  routeConfigs: RouteConfig[] = routes,
): RouteConfig | undefined => {
  return routeConfigs.find((route) => route.path === path);
};

// Utility: Check if user can access a specific route
export const canAccessRoute = (
  route: RouteConfig,
  userPermissions: string[],
  isSuperAdmin: boolean = false,
  userRoleValue?: string,
): boolean => {
  // Super superAdmin has access to everything
  if (isSuperAdmin) {
    return true;
  }

  // Legacy superAdmin support: if role value is "superAdmin" and no permissions, grant access
  if (
    userRoleValue === "superAdmin" &&
    (!userPermissions || userPermissions.length === 0)
  ) {
    return true;
  }

  // If no permissions required, route is accessible
  if (!route.permissions || route.permissions.length === 0) {
    return true;
  }

  // User needs at least one of the required permissions (OR logic)
  return route.permissions.some((perm) => userPermissions.includes(perm));
};

// Utility: Get all unique permissions from routes
export const getAllPermissions = (
  routeConfigs: RouteConfig[] = routes,
): string[] => {
  const permissions = new Set<string>();
  routeConfigs.forEach((route) => {
    if (route.permissions) {
      route.permissions.forEach((perm) => permissions.add(perm));
    }
  });
  return Array.from(permissions).sort();
};
